import { Button } from "@/components/ui/button";
import { Car, User, LogOut, Heart, Settings, Mail, Shield, LayoutDashboard, ChevronDown, PlusCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";
import { useUserRole } from "@/hooks/useUserRole";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


export default function Header() {
  const navigate = useNavigate();
  const { user, loading } = useSession();
  const { data: roleData } = useUserRole();

  async function handleBecomeHost() {
    if (user) navigate("/create-listing");
    else navigate("/auth?mode=signin&redirect=/create-listing");
  }

  async function handleSignOut() {
    try {
      // Sign out with specific scope to only clear local session if server fails
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.warn("Server logout failed, clearing local session:", error);
      }
    } catch (error) {
      // If any error occurs, log it but continue with local cleanup
      console.warn("Logout error:", error);
    } finally {
      // Always clear local storage and state
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
      // Navigate to home and reload to clear all state
      window.location.href = "/";
    }
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <Car className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold text-foreground">SharePark</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          <Link
            to="/search"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Find Plads
          </Link>
          <Link
            to="/parkering-koebenhavn"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            København
          </Link>

          {user && (
            <Link
              to="/favorites"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Favoritter
            </Link>
          )}

          <Button variant="ghost" onClick={handleBecomeHost} className="text-sm font-medium">
            Bliv Udlejer
          </Button>

          <Link
            to="/contact"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Kontakt
          </Link>
        </nav>

        {/* Right side */}
        <div className="flex items-center space-x-2">
          {!loading && user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 rounded-full px-3 shadow-sm">
                    <User className="h-4 w-4 mr-2" />
                    Min konto
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-popover">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-foreground">Min konto</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {roleData?.isAdmin && (
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/admin" className="flex items-center w-full">
                        <Shield className="h-4 w-4 mr-3 text-muted-foreground" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/dashboard" className="flex items-center w-full">
                      <LayoutDashboard className="h-4 w-4 mr-3 text-muted-foreground" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/create-listing" className="flex items-center w-full">
                      <PlusCircle className="h-4 w-4 mr-3 text-muted-foreground" />
                      Opret plads
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/profile" className="flex items-center w-full">
                      <User className="h-4 w-4 mr-3 text-muted-foreground" />
                      Profil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/favorites" className="flex items-center w-full">
                      <Heart className="h-4 w-4 mr-3 text-muted-foreground" />
                      Favoritter
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/contact" className="flex items-center w-full">
                      <Mail className="h-4 w-4 mr-3 text-muted-foreground" />
                      Kontakt support
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive">
                    <LogOut className="h-4 w-4 mr-3" />
                    Log ud
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/auth?mode=signin">
                <Button variant="ghost" size="sm" className="hidden md:flex">
                  <User className="h-4 w-4 mr-2" />
                  Log Ind
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button size="sm">Tilmeld Dig</Button>
              </Link>
            </>
          )}

        </div>
      </div>
    </header>
  );
}
